// Mixed News Component
// Displays a list of news items with filtering and pagination based on various tags
class MixedNewsComponent {
    constructor() {
        this.component = null;
        this.scEditorMode = null;
        this.selectedFilter = 'All';
        this.currentPage = 1;
        this.mobileInitialised = false;
        this.desktopInitialised = false;
        this.isLoading = false;
        this.abortController = null;

        // Cache DOM elements
        this.elements = {
            newsContentWrapper: null,
            paginationComponent: null,
            loadWrapper: null,
            loadMsg: null
        };

        // Debounced resize handler
        this.debouncedOrientationHandler = this.debounce(this.handleOrientationChange.bind(this), 150);

        this.init();
    }

    // Utility: Debounce function
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    // Utility: Create pagination icon
    createPaginationIcon(buttonClass, iconClass, tooltipText) {
        const btn = document.createElement('button');
        btn.className = buttonClass;
        btn.setAttribute('aria-label', tooltipText);

        const icon = document.createElement('span');
        icon.className = iconClass;
        icon.setAttribute('aria-hidden', 'true');

        const tooltip = document.createElement('span');
        tooltip.className = 'visually-hidden';
        tooltip.textContent = tooltipText;

        btn.append(icon, tooltip);
        return btn;
    }

    // Initialize component
    init() {
        this.component = document.querySelector('.mixed-news-component');
        if (!this.component) return;

        this.scEditorMode = document.querySelector('.sc-editor-mode');
        this.setupDOM();
        this.initializeView();
        this.addEventListeners();
    }

    // Setup DOM structure
    setupDOM() {
        // Create loader elements
        const loadCirc = document.createElement('div');
        loadCirc.className = 'list-loader';
        loadCirc.setAttribute('role', 'status');
        loadCirc.setAttribute('aria-live', 'polite');

        this.elements.loadMsg = document.createElement('span');
        this.elements.loadWrapper = document.createElement('div');
        this.elements.loadWrapper.className = 'loader-wrapper';
        this.elements.loadWrapper.append(loadCirc, this.elements.loadMsg);

        // Create main content areas
        this.elements.newsContentWrapper = document.createElement('div');
        this.elements.newsContentWrapper.className = 'news-content-wrapper';
        this.elements.newsContentWrapper.setAttribute('role', 'main');
        this.elements.newsContentWrapper.setAttribute('aria-live', 'polite');

        this.elements.paginationComponent = document.createElement('nav');
        this.elements.paginationComponent.id = 'pagination';
        this.elements.paginationComponent.setAttribute('role', 'navigation');
        this.elements.paginationComponent.setAttribute('aria-label', 'News pagination');

        this.component.append(this.elements.newsContentWrapper, this.elements.paginationComponent);
    }

    // Add event listeners
    addEventListeners() {
        if (typeof windowUtils !== 'undefined' && windowUtils.addOrientationListener) {
            windowUtils.addOrientationListener(this.debouncedOrientationHandler);
        } else {
            // Fallback for orientation changes
            window.addEventListener('resize', this.debouncedOrientationHandler);
        }
    }

    // Handle orientation changes
    handleOrientationChange() {
        if (this.isMobileView()) {
            if (!this.mobileInitialised) {
                this.initializeMobileNews();
            } else {
                this.setActiveMobileFilter();
            }
        } else {
            if (!this.desktopInitialised) {
                this.initializeDesktopNews();
            } else {
                this.setActiveDesktopFilter();
            }
        }
    }

    // Check if mobile view
    isMobileView() {
        return window.matchMedia("(max-width: 1023px)").matches;
    }

    // Initialize appropriate view
    initializeView() {
        if (this.isMobileView()) {
            this.initializeMobileNews();
        } else {
            this.initializeDesktopNews();
        }
    }

    // API: Fetch news data with proper error handling
    async fetchNewsData(filterTag) {
        if (this.isLoading) {
            this.abortController?.abort();
        }

        this.isLoading = true;
        this.abortController = new AbortController();

        try {
            this.showLoader();

            const newsPerPageElement = document.querySelector('.newsPerPage');
            if (!newsPerPageElement) {
                throw new Error('News per page configuration not found');
            }

            const newsPerPage = newsPerPageElement.value;
            const url = `${APIPrefix.get()}/search_api/genericnews?filterTag=${filterTag}&noOfItemsPerPage=${newsPerPage}`;

            const response = await fetch(url, {
                signal: this.abortController.signal,
                headers: {
                    'Accept': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            this.hideLoader();
            return data;

        } catch (error) {
            this.isLoading = false;
            if (error.name === 'AbortError') {
                console.log('Request aborted');
                return null;
            }
            this.showError(error.message);
            throw error;
        } finally {
            this.isLoading = false;
        }
    }

    // Show loading state
    showLoader() {
        const successMsg = document.querySelector('.rest-message--success');
        if (successMsg) {
            this.elements.loadMsg.textContent = successMsg.value;
        }
        this.elements.loadMsg.className = 'loader-title';
        this.component.appendChild(this.elements.loadWrapper);
        this.elements.newsContentWrapper.style.display = 'none';
        this.elements.paginationComponent.style.display = 'none';
    }

    // Show error state
    showError(message) {
        const failMsg = document.querySelector('.rest-message--fail');
        this.elements.loadMsg.textContent = failMsg ? failMsg.value : message;
        this.elements.loadMsg.className = 'error-title';
        this.elements.newsContentWrapper.style.display = 'none';

        // Remove loading circle if it exists
        const loadCirc = this.elements.loadWrapper.querySelector('.list-loader');
        if (loadCirc && loadCirc.parentNode) {
            loadCirc.parentNode.removeChild(loadCirc);
        }
    }

    // Hide loader
    hideLoader() {
        if (this.elements.loadWrapper.parentNode) {
            this.elements.loadWrapper.parentNode.removeChild(this.elements.loadWrapper);
        }
        this.elements.newsContentWrapper.style.display = 'flex';
        this.elements.paginationComponent.style.display = 'flex';
    }

    // Render news items
    renderNewsItems(items, page, itemsPerPage) {
        this.elements.newsContentWrapper.innerHTML = '';

        const start = (page - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        const paginatedItems = items.slice(start, end);

        if (paginatedItems.length === 0) {
            const emptyMessage = document.createElement('p');
            emptyMessage.textContent = 'No news items found.';
            emptyMessage.className = 'no-items';
            this.elements.newsContentWrapper.appendChild(emptyMessage);
            return;
        }

        const fragment = document.createDocumentFragment();

        paginatedItems.forEach(item => {
            const wrapper = document.createElement('div');
            wrapper.className = 'mixed-news-wrapper';

            let content = '';

            if (item.image?.path) {
                content += `<a href="${item.button?.url || ''}" aria-label="Read more about ${item.title || ''}">
                    <img src="${item.image.path}" alt="${item.image.alt || ''}" title="${item.title || ''}">
                </a>`;
            }

            content += `
                <p class="mixed-news-date">${item.date || ''}</p>
                <h3 class="mixed-news-title">${item.title || ''}</h3>
                <p class="mixed-news-desc">${item.shortDescription || ''}</p>
                <a class="mixed-news-cta button" href="${item.button?.url || ''}"
                   title="${item.button?.tooltip || ''}">${item.button?.name || 'Read More'}</a>
            `;

            wrapper.innerHTML = content;
            fragment.appendChild(wrapper);
        });

        this.elements.newsContentWrapper.appendChild(fragment);
    }

    // Set selected filter
    setSelectedFilter(filterValue) {
        this.selectedFilter = filterValue;
        this.currentPage = 1; // Reset to first page

        if (this.isMobileView()) {
            this.setActiveMobileFilter();
        } else {
            this.setActiveDesktopFilter();
        }
    }

    // Set active desktop filter
    setActiveDesktopFilter() {
        const filterBtns = document.querySelectorAll('.news-tag-btn');

        // Remove active class from all buttons
        filterBtns.forEach(btn => {
            btn.classList.remove('active');
            btn.setAttribute('aria-pressed', 'false');
        });

        // Find and activate the correct button
        filterBtns.forEach(btn => {
            if (btn.dataset.tag === this.selectedFilter) {
                btn.classList.add('active');
                btn.setAttribute('aria-pressed', 'true');
            }
        });

        this.loadFilteredData(this.selectedFilter);
    }

    // Set active mobile filter
    setActiveMobileFilter() {
        const selectFilter = document.querySelector('#mixedFiltersMobile');
        if (!selectFilter) return;

        // Set the dropdown to the saved value
        for (let i = 0; i < selectFilter.options.length; i++) {
            if (selectFilter.options[i].value === this.selectedFilter) {
                selectFilter.selectedIndex = i;
                break;
            }
        }

        this.loadFilteredData(this.selectedFilter);
    }

    // Load filtered data
    async loadFilteredData(filterTag) {
        try {
            const data = await this.fetchNewsData(filterTag);
            if (!data) return; // Request was aborted

            const newsItems = data.data?.news || [];
            const config = data.data?.configuration || {};
            const itemsPerPage = config.itemsPerPage || 9;

            this.renderNewsItems(newsItems, this.currentPage, itemsPerPage);
            this.setupPagination(newsItems, itemsPerPage, config);

            // Show/hide pagination
            this.elements.paginationComponent.style.visibility =
                newsItems.length > itemsPerPage ? 'visible' : 'hidden';

            // Handle empty state
            if (newsItems.length === 0) {
                this.showEmptyState(config.headerTemplateMessage || 'No news items found.');
            }

        } catch (error) {
            console.error('Error loading news data:', error);
        }
    }

    // Show empty state
    showEmptyState(message) {
        const errorMsg = document.createElement('div');
        errorMsg.textContent = message;
        errorMsg.className = 'no-items';
        errorMsg.setAttribute('role', 'status');
        this.elements.newsContentWrapper.style.display = 'none';
        this.component.appendChild(errorMsg);
    }

    // Setup pagination based on view
    setupPagination(items, itemsPerPage, config) {
        this.elements.paginationComponent.innerHTML = '';

        const pageCount = Math.ceil(items.length / itemsPerPage);
        if (pageCount <= 1) return;

        if (this.isMobileView()) {
            this.setupMobilePagination(items, itemsPerPage, pageCount, config);
        } else {
            this.setupDesktopPagination(items, itemsPerPage, pageCount, config);
        }
    }

    // Setup desktop pagination with numbered buttons
    setupDesktopPagination(items, itemsPerPage, pageCount, config) {
        const fragment = document.createDocumentFragment();

        // Create navigation buttons
        const prevBtn = this.createPaginationIcon('page-prev', 'icon-single-chevron-left', config.tooltips?.prevPage || 'Previous page');
        const firstBtn = this.createPaginationIcon('page-first', 'icon-double-chevron-left', config.tooltips?.firstPage || 'First page');
        const nextBtn = this.createPaginationIcon('page-next', 'icon-single-chevron', config.tooltips?.nextPage || 'Next page');
        const lastBtn = this.createPaginationIcon('page-last', 'icon-double-chevron', config.tooltips?.lastPage || 'Last page');

        fragment.append(firstBtn, prevBtn);

        // Create numbered page buttons
        for (let i = 1; i <= pageCount; i++) {
            const pageBtn = this.createPageButton(i, items, itemsPerPage, config.tooltips?.page || 'Page');
            fragment.appendChild(pageBtn);
        }

        fragment.append(nextBtn, lastBtn);
        this.elements.paginationComponent.appendChild(fragment);

        // Add event listeners
        this.addPaginationListeners(items, itemsPerPage, pageCount, { prevBtn, nextBtn, firstBtn, lastBtn });
        this.updatePaginationState(pageCount, { prevBtn, nextBtn, firstBtn, lastBtn });
    }

    // Setup mobile pagination (navigation buttons only)
    setupMobilePagination(items, itemsPerPage, pageCount, config) {
        const fragment = document.createDocumentFragment();

        const prevBtn = this.createPaginationIcon('page-prev', 'icon-single-chevron-left', config.tooltips?.prevPage || 'Previous page');
        const firstBtn = this.createPaginationIcon('page-first', 'icon-double-chevron-left', config.tooltips?.firstPage || 'First page');
        const nextBtn = this.createPaginationIcon('page-next', 'icon-single-chevron', config.tooltips?.nextPage || 'Next page');
        const lastBtn = this.createPaginationIcon('page-last', 'icon-double-chevron', config.tooltips?.lastPage || 'Last page');

        fragment.append(firstBtn, prevBtn, nextBtn, lastBtn);
        this.elements.paginationComponent.appendChild(fragment);

        // Add event listeners with scroll to top
        this.addPaginationListeners(items, itemsPerPage, pageCount, { prevBtn, nextBtn, firstBtn, lastBtn }, true);
        this.updatePaginationState(pageCount, { prevBtn, nextBtn, firstBtn, lastBtn });
    }

    // Create page button
    createPageButton(pageNum, items, itemsPerPage, pageTooltip) {
        const btn = document.createElement('button');
        btn.innerHTML = `<span class="visually-hidden">${pageTooltip}</span> ${pageNum}`;
        btn.id = `page-${pageNum}`;
        btn.setAttribute('aria-label', `${pageTooltip} ${pageNum}`);

        // Ensure we're comparing numbers
        if (parseInt(this.currentPage) === parseInt(pageNum)) {
            btn.classList.add('active');
            btn.setAttribute('aria-current', 'page');
        }

        btn.addEventListener('click', () => {
            this.currentPage = parseInt(pageNum);
            this.renderNewsItems(items, this.currentPage, itemsPerPage);

            // Update both page button states AND navigation button states
            this.updatePageButtonStates();

            // Get the navigation buttons and update their states
            const navButtons = {
                firstBtn: this.elements.paginationComponent.querySelector('.page-first'),
                prevBtn: this.elements.paginationComponent.querySelector('.page-prev'),
                nextBtn: this.elements.paginationComponent.querySelector('.page-next'),
                lastBtn: this.elements.paginationComponent.querySelector('.page-last')
            };

            const pageCount = Math.ceil(items.length / itemsPerPage);
            this.updatePaginationState(pageCount, navButtons);
        });

        return btn;
    }

    // Add pagination event listeners
    addPaginationListeners(items, itemsPerPage, pageCount, buttons, scrollToTop = false) {
        const { prevBtn, nextBtn, firstBtn, lastBtn } = buttons;

        prevBtn.addEventListener('click', () => {
            if (this.currentPage > 1) {
                this.currentPage = Math.max(1, this.currentPage - 1);
                this.renderNewsItems(items, this.currentPage, itemsPerPage);
                this.updatePaginationState(pageCount, buttons);
                if (scrollToTop) this.scrollToTop();
            }
        });

        nextBtn.addEventListener('click', () => {
            if (this.currentPage < pageCount) {
                this.currentPage = Math.min(pageCount, this.currentPage + 1);
                this.renderNewsItems(items, this.currentPage, itemsPerPage);
                this.updatePaginationState(pageCount, buttons);
                if (scrollToTop) this.scrollToTop();
            }
        });

        firstBtn.addEventListener('click', () => {
            this.currentPage = 1;
            this.renderNewsItems(items, this.currentPage, itemsPerPage);
            this.updatePaginationState(pageCount, buttons);
            if (scrollToTop) this.scrollToTop();
        });

        lastBtn.addEventListener('click', () => {
            this.currentPage = parseInt(pageCount);
            this.renderNewsItems(items, this.currentPage, itemsPerPage);
            this.updatePaginationState(pageCount, buttons);
            if (scrollToTop) this.scrollToTop();
        });
    }

    // Update pagination button states
    updatePaginationState(pageCount, buttons) {
        const { prevBtn, nextBtn, firstBtn, lastBtn } = buttons;

        // Ensure we're working with numbers
        const currentPageNum = parseInt(this.currentPage);
        const totalPages = parseInt(pageCount);

        // Update first/previous button states
        [firstBtn, prevBtn].forEach(btn => {
            if (currentPageNum <= 1) {
                btn.classList.add('disabled');
                btn.setAttribute('tabindex', '-1');
                btn.setAttribute('aria-disabled', 'true');
            } else {
                btn.classList.remove('disabled');
                btn.setAttribute('tabindex', '0');
                btn.setAttribute('aria-disabled', 'false');
            }
        });

        // Update next/last button states
        [nextBtn, lastBtn].forEach(btn => {
            if (currentPageNum >= totalPages) {
                btn.classList.add('disabled');
                btn.setAttribute('tabindex', '-1');
                btn.setAttribute('aria-disabled', 'true');
            } else {
                btn.classList.remove('disabled');
                btn.setAttribute('tabindex', '0');
                btn.setAttribute('aria-disabled', 'false');
            }
        });

        this.updatePageButtonStates();
    }

    // Update page button states (desktop only)
    updatePageButtonStates() {
        const pageButtons = this.elements.paginationComponent.querySelectorAll('button[id^="page-"]');
        pageButtons.forEach(btn => {
            const pageNum = parseInt(btn.id.replace('page-', ''));
            const currentPageNum = parseInt(this.currentPage);

            if (pageNum === currentPageNum) {
                btn.classList.add('active');
                btn.setAttribute('aria-current', 'page');
            } else {
                btn.classList.remove('active');
                btn.removeAttribute('aria-current');
            }
        });
    }

    // Smooth scroll to top
    scrollToTop() {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }

    // Initialize desktop news
    initializeDesktopNews() {
        const btnFilterWrapper = document.querySelector('.btn-filters-wrapper');
        if (!btnFilterWrapper) return;

        const allBtn = btnFilterWrapper.firstElementChild;

        if (!this.scEditorMode) {
            window.addEventListener('load', () => {
                if (this.selectedFilter === 'All' && allBtn) {
                    this.selectedFilter = allBtn.dataset.tag;
                }
                this.setActiveDesktopFilter();
            });
        }

        const filterBtns = document.querySelectorAll('.news-tag-btn');
        filterBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                this.setSelectedFilter(btn.dataset.tag);
            });
        });

        this.desktopInitialised = true;
    }

    // Initialize mobile news
    initializeMobileNews() {
        const selectFilter = document.querySelector('#mixedFiltersMobile');
        if (!selectFilter) return;

        if (!this.scEditorMode) {
            window.addEventListener('load', () => {
                this.setActiveMobileFilter();
            });
        }

        selectFilter.addEventListener('change', () => {
            const value = selectFilter.options[selectFilter.selectedIndex].value;
            this.setSelectedFilter(value);
        });

        this.mobileInitialised = true;
    }

    // Cleanup method
    destroy() {
        if (this.abortController) {
            this.abortController.abort();
        }

        if (typeof windowUtils !== 'undefined' && windowUtils.removeOrientationListener) {
            windowUtils.removeOrientationListener(this.debouncedOrientationHandler);
        } else {
            window.removeEventListener('resize', this.debouncedOrientationHandler);
        }
    }
}

// Initialize the component
document.addEventListener('DOMContentLoaded', () => {
    window.mixedNewsInstance = new MixedNewsComponent();
});

// Clean up on page unload
window.addEventListener('beforeunload', () => {
    if (window.mixedNewsInstance) {
        window.mixedNewsInstance.destroy();
    }
});